import { useNavigate } from 'react-router-dom';
import { Fragment, useCallback, useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import { InquisitiveActivitiesContentType } from 'global/graphql/scalars.type';
import { ActivityBuilderThemeContextProvider } from 'approot/activity-builder/theme/activity-builder-theme';
import { LessonActivitiesFragment_lessonActivities_sectionsOverview } from './sections/activities/__generated__/LessonActivitiesFragment';
import { getSectionsAndSubSectionsOverview } from 'approot/activity-builder/activity-builder.utils';
import { useRegionData } from 'approot/shared/region/region.apollo';
import {
  ActivityBuilderIntroPreview,
  ActivityBuilderPagePreview,
  HeaderFooterContentBase,
} from 'approot/activity-builder/page/activity-builder-page-preview';
import { LessonActivities_focusQuestion } from 'approot/activity-builder/__generated__/LessonActivities';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import { NEXT, PREV, useActivityPreview } from './activity-preview.hooks';
import {
  ActivityCarouselButton,
  ActivityThumbnailButton,
  ActivityThumbnailScroll,
} from './activity-preview-shared';
import { getActivityDetailsFromOverview } from 'approot/activity-builder/logic/get-activity-details-from-overview';
import { getActivityTitle } from 'approot/activity-builder/logic/get-activity-title';
import { getPresentationUrl } from 'approot/activity-builder/activity-builder-url.utils';
import {
  SimpleCard,
  SimpleCardButton,
} from 'approot/shared/simple-card/simple-card';
import {
  ACTIVITY_BUILDER_A4_LONG_SIDE,
  ACTIVITY_BUILDER_A4_SHORT_SIDE,
  SectionOrSubSectionOverview,
} from 'approot/activity-builder/activity-builder.constants';
import * as Styled from './activity-preview.style';
import ActivityCardActions from 'approot/shared/content-card/bookmark/activity-card-actions';
import { useAuthorisedAction } from 'approot/shared/hooks/authorised-action.hooks';
import {
  clickLink,
  goOrShowModal,
} from 'approot/shared/content-card/content-card.utils';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { ImagePicture } from 'approot/shared/images/images';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import {
  EVENT_ACTION_EXPAND,
  EVENT_CATEGORY_LESSON_NAV,
} from 'approot/shared/data-tracker/data-events.constants';
import { ASSESSMENT, LESSON, LESSON_ACTIVITIES } from 'global/constants';

import blurredGreenCirclePng from './images/blurred-tile-green-circle.png';
import blurredGreenCircleWebp from './images/blurred-tile-green-circle.webp';
import blurredGreenCircleAvif from './images/blurred-tile-green-circle.avif';

import blurredBlueSquarePng from './images/blurred-tile-blue-square.png';
import blurredBlueSquareWebp from './images/blurred-tile-blue-square.webp';
import blurredBlueSquareAvif from './images/blurred-tile-blue-square.avif';

import blurredBlackDiamondPng from './images/blurred-tile-black-diamond.png';
import blurredBlackDiamondWebp from './images/blurred-tile-black-diamond.webp';
import blurredBlackDiamondAvif from './images/blurred-tile-black-diamond.avif';

const INTRODUCTION = -1;

type Props = {
  contentId: number;
  contentType: InquisitiveActivitiesContentType;
  authorId?: number;
  slug: string;
  classId?: number;
  version?: string | null;
  overview: LessonActivitiesFragment_lessonActivities_sectionsOverview[] | null;
  baseFontSize?: number;
  focusQuestion?: LessonActivities_focusQuestion | null;
  phenomena?: LessonActivities_focusQuestion | null;
  landscapeImage?: {
    responsive: ResponsiveImage[] | null;
  } | null;
};

const LessonActivitiesPreviewGuest = ({
  contentId,
  contentType,
  authorId,
  slug,
  unitName,
  subjectName,
  contentName,
  classId,
  version,
  baseFontSize,
  overview,
  focusQuestion,
  phenomena,
  landscapeImage,
}: Props & HeaderFooterContentBase) => {
  const {
    regionBanner: { height },
  } = useRegionData();

  const { unauthorisedAccessHref } = useAuthorisedAction();
  const link = unauthorisedAccessHref();

  const navigate = useNavigate();

  const {
    thumbnailScrollRef,
    index,
    isLoading,
    scrollPosition,
    setScrollPosition,
    handleMoveThumbnails,
    handleOnScroll,
    handleThumbnailSelect,
  } = useActivityPreview({
    initialIndex: INTRODUCTION,
  });

  const sortedSectionsOverview = useMemo(() => sortBy(overview, 'sort'), [
    overview,
  ]);

  const sectionsAndSubSectionsOverview = useMemo(
    () => getSectionsAndSubSectionsOverview(sortedSectionsOverview),
    [sortedSectionsOverview]
  );

  const currentSlide = sectionsAndSubSectionsOverview[index];

  const imageRatio =
    (ACTIVITY_BUILDER_A4_SHORT_SIDE / ACTIVITY_BUILDER_A4_LONG_SIDE) * 100;

  const getTitle = useCallback(
    (section: SectionOrSubSectionOverview) => {
      const details = getActivityDetailsFromOverview({
        section,
        sectionsOverview: sortedSectionsOverview,
      });
      return getActivityTitle({
        differentiation: details.differentiation,
        activityNumber: details.activityNumber,
        progressLabel: details.progressLabel,
        totalSteps: details.totalSteps,
        contentType,
      });
    },
    [contentType, sortedSectionsOverview]
  );

  const pushExpandDataEvent = useCallback(
    (index: number) => {
      pushDataTrackerEvent(EVENT_CATEGORY_LESSON_NAV, EVENT_ACTION_EXPAND, {
        target_type: 'lesson-section',
        target_identifier: index + 1,
        content_id: contentId,
        content_type: contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
      });
    },
    [contentId, contentType]
  );

  if (sectionsAndSubSectionsOverview.length === 0) return null;

  return (
    <ActivityBuilderThemeContextProvider initialFontSize={baseFontSize}>
      <Styled.ActivityContainer bannerHeight={height}>
        <Styled.ActivityCarousel $isLoading={isLoading}>
          {index === INTRODUCTION ? (
            <SimpleCard
              ariaTitle="Introduction"
              link={getPresentationUrl({
                slug,
                userClassId: classId,
                contentType,
                version,
              })}
              imageRatio={imageRatio}
              bodyText={
                <ActivityBuilderIntroPreview
                  focusQuestion={focusQuestion || null}
                  phenomena={phenomena || null}
                  headerFooterContent={{
                    unitName,
                    subjectName,
                    contentName,
                    pageNumber: 1,
                  }}
                  images={landscapeImage?.responsive || []}
                  authorId={authorId}
                />
              }
              actionItems={
                <ActivityCardActions
                  contentId={contentId}
                  contentType={contentType}
                  canBookmark={false}
                  canLink
                  link={
                    link ||
                    getPresentationUrl({
                      slug,
                      userClassId: classId,
                      contentType,
                      version,
                    })
                  }
                  linkAriaLabel="Go to introduction"
                  onLinkClick={() => {
                    pushExpandDataEvent(index);
                    return goOrShowModal(link);
                  }}
                />
              }
              onClick={() => pushExpandDataEvent(index)}
            />
          ) : (
            <SimpleCard
              link={getPresentationUrl({
                sectionId: currentSlide.id,
                contentType,
                slug,
                userClassId: classId,
                version,
              })}
              ariaTitle={getTitle(currentSlide).title}
              imageRatio={imageRatio}
              bodyText={
                <ActivityBuilderPagePreview
                  id={currentSlide.id}
                  contentId={contentId}
                  parentElement={null}
                  contentType={contentType}
                  sectionOverviews={sortedSectionsOverview}
                  headerFooterContent={{
                    unitName,
                    subjectName,
                    contentName,
                    pageNumber: index + 2,
                  }}
                  version={version}
                />
              }
              actionItems={
                <ActivityCardActions
                  contentId={contentId}
                  contentType={contentType}
                  canLink={false}
                  canBookmark={false}
                  link={getPresentationUrl({
                    sectionId: currentSlide.id,
                    contentType,
                    slug,
                    userClassId: classId,
                    version,
                  })}
                  linkAriaLabel={`Go to: ${getTitle(currentSlide).title}`}
                  onLinkClick={() => pushExpandDataEvent(index)}
                />
              }
              onClick={() => pushExpandDataEvent(index)}
            />
          )}

          {index !== -1 && (
            <ActivityCarouselButton
              onClick={() => {
                link && index > 1
                  ? clickLink({ navigate, link })
                  : handleThumbnailSelect(index - 1);
              }}
            />
          )}
          {index !== sectionsAndSubSectionsOverview.length - 1 && (
            <ActivityCarouselButton
              isNext
              onClick={() => {
                link && index > 0
                  ? clickLink({ navigate, link })
                  : handleThumbnailSelect(index + 1);
              }}
            />
          )}
        </Styled.ActivityCarousel>

        <Styled.ActivityThumbnails>
          {!scrollPosition.start && (
            <ActivityThumbnailButton
              onClick={() => handleMoveThumbnails(PREV)}
            />
          )}
          {!scrollPosition.end && (
            <ActivityThumbnailButton
              isNext
              onClick={() => handleMoveThumbnails(NEXT)}
            />
          )}

          <ActivityThumbnailScroll
            ref={thumbnailScrollRef}
            handleOnScroll={handleOnScroll}
            setScrollPosition={setScrollPosition}
          >
            <Styled.ActivityThumbnailsWrapper>
              <Styled.ActivityThumbnail id={`activity-${INTRODUCTION}`}>
                <SimpleCardButton
                  ariaTitle="Introduction"
                  onClick={() => handleThumbnailSelect(INTRODUCTION)}
                  imageRatio={imageRatio}
                  isSelected={INTRODUCTION === index}
                  bodyText={
                    <ActivityBuilderIntroPreview
                      focusQuestion={focusQuestion || null}
                      phenomena={phenomena || null}
                      introBackground={
                        landscapeImage?.responsive?.[0]?.urls?.[0].url
                      }
                      headerFooterContent={{
                        unitName,
                        subjectName,
                        contentName,
                        pageNumber: 1,
                      }}
                      authorId={authorId}
                    />
                  }
                  actionItems={
                    <ActivityCardActions
                      canLink={false}
                      contentId={contentId}
                      contentType={contentType}
                      canBookmark={false}
                      link={getPresentationUrl({
                        contentType,
                        slug,
                        userClassId: classId,
                        version,
                      })}
                    />
                  }
                />
              </Styled.ActivityThumbnail>

              {sectionsAndSubSectionsOverview?.map((section, pageIndex) => {
                const differentiation =
                  'parentSectionId' in section
                    ? sectionsAndSubSectionsOverview.find(
                        s => s.id === section.parentSectionId
                      )?.differentiation
                    : section.differentiation;
                return (
                  <Fragment key={section.id}>
                    {pageIndex < 2 ? (
                      <Styled.ActivityThumbnail id={`activity-${pageIndex}`}>
                        <SimpleCardButton
                          ariaTitle={getTitle(section).title}
                          onClick={() => {
                            handleThumbnailSelect(pageIndex);
                          }}
                          imageRatio={imageRatio}
                          isSelected={pageIndex === index}
                          bodyText={
                            <ActivityBuilderPagePreview
                              id={section.id}
                              contentId={contentId}
                              parentElement={null}
                              contentType={contentType}
                              sectionOverviews={sortedSectionsOverview}
                              headerFooterContent={{
                                unitName,
                                subjectName,
                                contentName,
                                pageNumber: pageIndex + 2,
                              }}
                              version={version}
                            />
                          }
                        />
                      </Styled.ActivityThumbnail>
                    ) : (
                      <Styled.ActivityThumbnail id={`activity-${pageIndex}`}>
                        <SimpleCardButton
                          ariaTitle={getTitle(section).title}
                          onClick={() => {
                            link
                              ? clickLink({ navigate, link })
                              : handleThumbnailSelect(pageIndex);
                          }}
                          imageRatio={imageRatio}
                          isSelected={pageIndex === index}
                          bodyText={
                            <>
                              {differentiation ===
                                Differentiation.GreenCircle && (
                                <ImagePicture
                                  avif={{ path: blurredGreenCircleAvif }}
                                  webp={{ path: blurredGreenCircleWebp }}
                                  png={{ path: blurredGreenCirclePng }}
                                  altText="Locked Green Circle activity. Sign in or reactivate to see all content."
                                />
                              )}

                              {differentiation ===
                                Differentiation.BlueSquare && (
                                <ImagePicture
                                  avif={{ path: blurredBlueSquareAvif }}
                                  webp={{ path: blurredBlueSquareWebp }}
                                  png={{ path: blurredBlueSquarePng }}
                                  altText="Locked Blue Square activity. Sign in or reactivate to see all content."
                                />
                              )}

                              {differentiation ===
                                Differentiation.BlackDiamond && (
                                <ImagePicture
                                  avif={{ path: blurredBlackDiamondAvif }}
                                  webp={{ path: blurredBlackDiamondWebp }}
                                  png={{ path: blurredBlackDiamondPng }}
                                  altText="Locked Black Diamond activity. Sign in or reactivate to see all content."
                                />
                              )}
                            </>
                          }
                        />
                      </Styled.ActivityThumbnail>
                    )}
                  </Fragment>
                );
              })}
            </Styled.ActivityThumbnailsWrapper>
          </ActivityThumbnailScroll>
        </Styled.ActivityThumbnails>
      </Styled.ActivityContainer>
    </ActivityBuilderThemeContextProvider>
  );
};

export default LessonActivitiesPreviewGuest;
